import React, { useState, useEffect } from 'react';
import {
  FiCreditCard, FiTruck, FiPlus, FiTrash2, FiSave,
  FiSettings, FiMessageCircle, FiSmartphone, FiToggleLeft, FiToggleRight, FiLock,
} from 'react-icons/fi';
import { adminApi } from '../adminApi';
import '../admin.css';
import './AdminSettings.css';

const SECTIONS = [
  { id: 'payment',  label: 'Payment Methods', icon: FiCreditCard,    desc: 'Accounts shown at checkout' },
  { id: 'shipping', label: 'Shipping',         icon: FiTruck,         desc: 'Delivery cost & options' },
  { id: 'whatsapp', label: 'WhatsApp',         icon: FiMessageCircle, desc: 'Order notification number' },
  { id: 'security', label: 'Security',         icon: FiLock,          desc: 'Change username & password' },
];

const TYPE_META = {
  bank:      { label: 'Bank Account',     icon: FiCreditCard,   showBankName: true  },
  jazzcash:  { label: 'JazzCash Account', icon: FiSmartphone,   showBankName: false },
  easypaisa: { label: 'Easypaisa Account',icon: FiSmartphone,   showBankName: false },
  custom:    { label: 'Custom Account',   icon: FiCreditCard,   showBankName: false },
};

const DEFAULT_METHODS = [
  { id: 'bank-1',      type: 'bank',      label: 'Bank Account',      bank_name: '', account_number: '', account_title: '', enabled: true  },
  { id: 'jazzcash-1',  type: 'jazzcash',  label: 'JazzCash Account',  bank_name: '', account_number: '', account_title: '', enabled: true  },
  { id: 'easypaisa-1', type: 'easypaisa', label: 'Easypaisa Account', bank_name: '', account_number: '', account_title: '', enabled: true  },
];

const EMPTY_METHOD = (type = 'custom') => ({
  id: String(Date.now()),
  type,
  label: TYPE_META[type]?.label || '',
  bank_name: '',
  account_number: '',
  account_title: '',
  enabled: true,
});

function PaymentSection({ methods, onChange }) {
  const update  = (id, key, val) => onChange(methods.map(m => m.id === id ? { ...m, [key]: val } : m));
  const toggle  = (id) => update(id, 'enabled', !methods.find(m => m.id === id)?.enabled);
  const remove  = (id) => onChange(methods.filter(m => m.id !== id));

  const [addType, setAddType] = useState('bank');

  return (
    <div className="as-section-body">
      <p className="as-section-desc">
        Toggle which accounts are visible at checkout. Disabled accounts are saved but not shown to customers.
      </p>

      {methods.map((m) => {
        const meta = TYPE_META[m.type] || TYPE_META.custom;
        const Icon = meta.icon;
        return (
          <div key={m.id} className={`as-card ${m.enabled ? '' : 'as-card-disabled'}`}>
            <div className="as-card-header">
              <div className="as-card-title">
                <div className="as-card-icon"><Icon size={14} /></div>
                <input
                  className="as-name-input"
                  value={m.label}
                  onChange={e => update(m.id, 'label', e.target.value)}
                  placeholder={meta.label}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button className={`as-toggle-btn ${m.enabled ? 'on' : 'off'}`} onClick={() => toggle(m.id)}>
                  {m.enabled ? <FiToggleRight size={22} /> : <FiToggleLeft size={22} />}
                  <span>{m.enabled ? 'Visible' : 'Hidden'}</span>
                </button>
                <button className="as-remove-btn" onClick={() => remove(m.id)}>
                  <FiTrash2 size={13} />
                </button>
              </div>
            </div>

            <div className="as-grid-2">
              {meta.showBankName && (
                <div className="as-field">
                  <label>Bank Name</label>
                  <input value={m.bank_name} onChange={e => update(m.id, 'bank_name', e.target.value)} placeholder="Enter bank name" />
                </div>
              )}
              <div className="as-field">
                <label>Account Number</label>
                <input value={m.account_number} onChange={e => update(m.id, 'account_number', e.target.value.replace(/-/g, ''))} placeholder="Enter account number" />
              </div>
              <div className="as-field">
                <label>Account Title</label>
                <input value={m.account_title} onChange={e => update(m.id, 'account_title', e.target.value)} placeholder="Enter account title" />
              </div>
            </div>
          </div>
        );
      })}

      <div className="as-add-row">
        <select className="as-type-select" value={addType} onChange={e => setAddType(e.target.value)}>
          <option value="bank">Bank Account</option>
          <option value="jazzcash">JazzCash Account</option>
          <option value="easypaisa">Easypaisa Account</option>
          <option value="custom">Custom</option>
        </select>
        <button className="as-add-btn" onClick={() => onChange([...methods, EMPTY_METHOD(addType)])}>
          <FiPlus size={14} /> Add Account
        </button>
      </div>
    </div>
  );
}

function WhatsAppSection({ number, onChange }) {
  return (
    <div className="as-section-body">
      <p className="as-section-desc">
        After placing an online banking order, customers are prompted to send their payment proof to this number.
      </p>
      <div className="as-card">
        <div className="as-card-header">
          <div className="as-card-title">
            <div className="as-card-icon"><FiMessageCircle size={14} /></div>
            WhatsApp Number
          </div>
        </div>
        <div className="as-field" style={{ maxWidth: 320 }}>
          <label>Number <span className="as-hint">include country code</span></label>
          <input value={number} onChange={e => onChange(e.target.value)} placeholder="Enter WhatsApp number" />
        </div>
        {number && (
          <p style={{ margin: '10px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Link: <span style={{ color: 'var(--text)', fontWeight: 600 }}>
              https://wa.me/{number.replace(/\D/g, '')}
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

function SecuritySection() {
  const [form, setForm]     = useState({ current_password: '', new_username: '', new_password: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.current_password) return setMsg({ ok: false, text: 'Current password required' });
    if (!form.new_username && !form.new_password) return setMsg({ ok: false, text: 'Enter new username or password' });
    setSaving(true);
    setMsg(null);
    try {
      await adminApi.updateCredentials(form);
      setMsg({ ok: true, text: 'Credentials updated — please log in again' });
      setForm({ current_password: '', new_username: '', new_password: '' });
      setTimeout(() => { localStorage.removeItem('seoul-admin'); window.location.href = '/login'; }, 1500);
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || 'Failed to update' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="as-section-body">
      <p className="as-section-desc">Change your admin username or password. You will be logged out after saving.</p>
      <div className="as-card" style={{ maxWidth: 420 }}>
        <div className="as-field">
          <label>Current Password</label>
          <input type="password" value={form.current_password} onChange={e => update('current_password', e.target.value)} placeholder="Enter current password" />
        </div>
        <div className="as-field">
          <label>New Username <span className="as-hint">leave blank to keep current</span></label>
          <input value={form.new_username} onChange={e => update('new_username', e.target.value)} placeholder="Enter new username" />
        </div>
        <div className="as-field">
          <label>New Password <span className="as-hint">leave blank to keep current</span></label>
          <input type="password" value={form.new_password} onChange={e => update('new_password', e.target.value)} placeholder="Enter new password" />
        </div>
        {msg && <p style={{ margin: '8px 0 0', fontSize: '0.82rem', color: msg.ok ? 'var(--success, green)' : 'var(--danger, red)' }}>{msg.text}</p>}
        <div style={{ marginTop: 16 }}>
          <button className="btn-admin btn-admin-primary" onClick={save} disabled={saving}>
            <FiLock size={14} /> {saving ? 'Saving…' : 'Update Credentials'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShippingSection({ cost, onChange }) {
  return (
    <div className="as-section-body">
      <p className="as-section-desc">
        This amount is added to every order total at checkout.
      </p>
      <div className="as-card">
        <div className="as-card-header">
          <div className="as-card-title">
            <div className="as-card-icon"><FiTruck size={14} /></div>
            Delivery Cost
          </div>
        </div>
        <div className="as-field" style={{ maxWidth: 260 }}>
          <label>Cost per order (Rs)</label>
          <input type="number" min="0" value={cost} onChange={e => onChange(e.target.value)} placeholder="200" />
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [active,   setActive]   = useState('payment');
  const [methods,  setMethods]  = useState(DEFAULT_METHODS);
  const [shipping, setShipping] = useState(200);
  const [whatsapp, setWhatsapp] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [saveErr,  setSaveErr]  = useState('');

  useEffect(() => {
    adminApi.getStoreSettings()
      .then(r => {
        const s = r.data?.settings || {};
        setMethods(s.payment_methods?.length ? s.payment_methods : DEFAULT_METHODS);
        setShipping(s.shipping?.cost ?? 200);
        setWhatsapp(s.whatsapp_number ?? '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.getStoreSettings();
      const current = r.data?.settings || {};
      await adminApi.saveStoreSettings({
        settings: {
          ...current,
          payment_methods: methods,
          shipping: { cost: Number(shipping) },
          whatsapp_number: whatsapp,
        },
      });
      setSaved(true); setSaveErr('');
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setSaveErr('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="admin-loading"><div className="admin-spinner" /><p>Loading settings…</p></div>
  );

  return (
    <div className="as-page">
      <div className="as-header">
        <div className="as-header-left">
          <div className="as-header-icon"><FiSettings size={18} /></div>
          <div>
            <h1 className="as-header-title">Settings</h1>
            <p className="as-header-sub">Manage payment, shipping, and store preferences</p>
          </div>
        </div>
        <button className="btn-admin btn-admin-primary" onClick={save} disabled={saving}>
          <FiSave size={14} />
          {saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save Changes'}
        </button>
      </div>

      {saveErr && <div className="admin-error" style={{ margin: '0 0 12px' }}>{saveErr}</div>}
      <div className="as-body">
        <nav className="as-nav">
          {SECTIONS.map(sec => (
            <button
              key={sec.id}
              className={`as-nav-item ${active === sec.id ? 'active' : ''}`}
              onClick={() => setActive(sec.id)}
            >
              <sec.icon size={15} className="as-nav-icon" />
              <div className="as-nav-text">
                <span className="as-nav-label">{sec.label}</span>
                <span className="as-nav-desc">{sec.desc}</span>
              </div>
            </button>
          ))}
        </nav>

        <div className="as-content">
          {active === 'payment'  && <PaymentSection  methods={methods}  onChange={setMethods}  />}
          {active === 'shipping' && <ShippingSection cost={shipping}   onChange={setShipping} />}
          {active === 'whatsapp' && <WhatsAppSection number={whatsapp} onChange={setWhatsapp} />}
          {active === 'security' && <SecuritySection />}
        </div>
      </div>
    </div>
  );
}
